import React, { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Typography, Button, Descriptions, Row, Col, Space, Spin, Tag, Tabs, message } from 'antd';
import { ArrowLeftOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { Payable, PayableStatus, PayableType } from '@/types/finance';
import { getPayableById, deletePayable, updatePayable } from '@/api/finance';
import { useAuth } from '@/contexts/AuthContext';
import DeleteConfirmationModal from '../../components/common/DeleteConfirmationModal';
import PayableModal from '../../components/features/finance/PayableModal';
import ReceiptFormSection from '../../components/common/ReceiptFormSection';
import dayjs from 'dayjs';
import { Money } from '@/types';
import { formatFirestoreTimestamp } from '@/utils/date';

const { Title, Text } = Typography;

const PayableDetailsPage: React.FC = () => {
  const { t } = useTranslation(['payables', 'common']);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { firestoreUser } = useAuth();
  const queryClient = useQueryClient();
  const state = location.state as { payable: Payable } | null;
  
  // State
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [receipts, setReceipts] = useState(state?.payable?.receipts || []);
  
  // Queries
  const { data: payable, isLoading } = useQuery({
    queryKey: ['payable', id],
    queryFn: () => getPayableById(id!, firestoreUser!),
    enabled: !!id && !!firestoreUser && !state?.payable, // Only fetch if no state data
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Mutations
  const deleteMutation = useMutation({
    mutationFn: (id: string) => deletePayable(id, firestoreUser!),
    onSuccess: () => {
      message.success(t('payables:messages.deleteSuccess'));
      queryClient.invalidateQueries({ queryKey: ['payables'] });
      navigate('/finance/payables');
    },
    onError: () => {
      message.error(t('payables:messages.deleteError'));
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Payable> }) =>
      updatePayable(id, data, firestoreUser!),
    onSuccess: () => {
      message.success(t('payables:messages.updateSuccess'));
      queryClient.invalidateQueries({ queryKey: ['payable', id] });
      queryClient.invalidateQueries({ queryKey: ['payables'] });
      setEditModalVisible(false);
    },
    onError: () => {
      message.error(t('payables:messages.updateError'));
    },
  });

  // Use state data if available, otherwise use fetched data
  const displayPayable = state?.payable || payable;

  // Update receipts when displayPayable changes
  React.useEffect(() => {
    if (displayPayable?.receipts) {
      setReceipts(displayPayable.receipts);
    }
  }, [displayPayable]);

  const handleReceiptsChange = async (newReceipts: any[]) => {
    setReceipts(newReceipts);
    
    // Update the payable with new receipts
    if (id) {
      try {
        await updateMutation.mutateAsync({
          id,
          data: { receipts: newReceipts },
        });
      } catch (error) {
        console.error('Error updating receipts:', error);
      }
    }
  };

  // Loading state - only show if no state data is available
  if (isLoading && !state?.payable) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!displayPayable) {
    return <Card>Payable not found</Card>;
  }

  const formatMoney = (amount: Money) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: amount.currency || 'USD'
    }).format(amount.amount);
  };

  // Helper function to safely format values for display
  const formatValue = (value: any): string => {
    if (value === null || value === undefined) {
      return 'N/A';
    }
    
    // Handle Firestore timestamp objects
    if (value && typeof value === 'object' && 'seconds' in value && 'nanoseconds' in value) {
      try {
        const date = formatFirestoreTimestamp(value);
        return date ? dayjs(date).format('MMM DD, YYYY HH:mm') : 'Invalid Date';
      } catch (error) {
        return 'Invalid Date';
      }
    }
    
    // Handle Money objects
    if (value && typeof value === 'object' && 'amount' in value && 'currency' in value) {
      return formatMoney(value);
    }
    
    // Handle other objects
    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2);
    }
    
    return String(value);
  };

  const getStatusTagColor = (status: PayableStatus) => {
    switch (status) {
      case 'pending': return 'orange';
      case 'partial': return 'blue';
      case 'paid': return 'green';
      case 'overdue': return 'red';
      case 'cancelled': return 'default';
      default: return 'default';
    }
  };

  const getTypeTagColor = (type: PayableType) => {
    switch (type) {
      case 'vendor': return 'blue';
      case 'asset': return 'green';
      case 'employee': return 'purple';
      case 'payroll': return 'orange';
      case 'maintenance': return 'red';
      case 'other': return 'default';
      default: return 'default';
    }
  };

  const handleEdit = () => {
    setEditModalVisible(true);
  };

  const handleEditSuccess = async (payableData: Omit<Payable, 'id' | 'activityRecords' | 'workspace'>) => {
    if (id) {
      await updateMutation.mutateAsync({
        id,
        data: payableData,
      });
    }
  };

  const handleEditCancel = () => {
    setEditModalVisible(false);
  };

  const handleDelete = () => {
    setDeleteModalVisible(true);
  };

  const handleDeleteConfirm = () => {
    if (id) {
      deleteMutation.mutate(id);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalVisible(false);
  };

  const handleBack = () => {
    navigate(-1);
  };

  const items = [
    {
      key: 'details',
      label: t('common:details'),
      children: (
        <Descriptions bordered column={1}>
          <Descriptions.Item label={t('common:name')}>
            {displayPayable.name}
          </Descriptions.Item>
          
          <Descriptions.Item label={t('common:typeLabel')}>
            <Tag color={getTypeTagColor(displayPayable.type)}>
              {t(`payables:types.${displayPayable.type}`)}
            </Tag>
          </Descriptions.Item>
          
          <Descriptions.Item label={t('common:amount')}>
            <span style={{ fontWeight: 500, color: '#1890ff' }}>
              {formatMoney(displayPayable.amount)}
            </span>
          </Descriptions.Item>
          
          <Descriptions.Item label={t('common:dueDate')}>
            {displayPayable.dueDate ? dayjs(formatFirestoreTimestamp(displayPayable.dueDate)).format('MMM DD, YYYY') : '-'}
          </Descriptions.Item>
          
          <Descriptions.Item label={t('common:statusLabel')}>
            <Tag color={getStatusTagColor(displayPayable.status)}>
              {t(`payables:statuses.${displayPayable.status}`)}
            </Tag>
          </Descriptions.Item>
          
          <Descriptions.Item label={t('common:description')}>
            {displayPayable.description}
          </Descriptions.Item>
          
          {displayPayable.notes && (
            <Descriptions.Item label={t('common:notes')}>
              {displayPayable.notes}
            </Descriptions.Item>
          )}
        </Descriptions>
      ),
    },
    {
      key: 'receipts',
      label: t('common:receipts'),
      children: (
        <ReceiptFormSection 
          receipts={receipts}
          onReceiptsChange={handleReceiptsChange}
        />
      ),
    },
    {
      key: 'activity',
      label: t('common:activityLog'),
      children: (
        <div>
          {displayPayable.activityRecords && displayPayable.activityRecords.length > 0 ? (
            <div>
              {displayPayable.activityRecords.map((record: any, index: number) => (
                <div key={index} style={{ marginBottom: '16px', padding: '12px', border: '1px solid #f0f0f0', borderRadius: '4px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <Text strong>{record.type}</Text>
                    <Text type="secondary">{dayjs(record.timestamp).format('YYYY-MM-DD HH:mm')}</Text>
                  </div>
                  <Text>{record.performedBy.name}</Text>
                  {record.changedFields && (
                    <div style={{ marginTop: '8px' }}>
                      {Object.entries(record.changedFields).map(([field, values]: [string, any]) => (
                        <div key={field} style={{ fontSize: '12px', color: '#666' }}>
                          {field}: {formatValue(values.oldValue)} → {formatValue(values.newValue)}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
              {t('common:noActivityLog')}
            </div>
          )}
        </div>
      ),
    },
  ];

  return (
    <div style={{ width: '100%' }}>
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
              {t('common:back')}
            </Button>
            <Title level={3}>{displayPayable.name}</Title>
          </Space>
        </Col>
        <Col>
          <Space>
            <Button icon={<EditOutlined />} onClick={handleEdit}>
              {t('common:actions.edit')}
            </Button>
            <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>
              {t('common:actions.delete')}
            </Button>
          </Space>
        </Col>
      </Row>
      
      {/* Content */}
      <Card>
        <Tabs items={items} />
      </Card>

      {/* Edit Modal */}
      <PayableModal
        open={editModalVisible}
        payable={displayPayable}
        onSuccess={handleEditSuccess}
        onCancel={handleEditCancel}
        loading={updateMutation.isPending}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        visible={deleteModalVisible}
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={t('payables:messages.deleteConfirmTitle', { name: displayPayable.name })}
        error={deleteMutation.error as any}
        onErrorDismiss={() => setDeleteModalVisible(false)}
      />
    </div>
  );
};

export default PayableDetailsPage; 